<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import { mapState } from 'vuex'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

import useJwt from '@/auth/jwt/useJwt'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import safePages from '@/router/routes/pages'
import secureCookies from './libs/cookies'
import secureLs from './libs/secureLs'
import { removeLocalStorageLogout } from './auth/utils'
import { initialAbility } from './libs/acl/config'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')

const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts

    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  data() {
    return {
      appVersion: process.env.VUE_APP_VERSION,
      menus: [],
      profiles: [],
      userData: null,
      abilityNew: [],
      productsNew: {
        komship: null,
        kompack: null,
        komcards: null,
        komchat: null,
        komads: null,
      },
      komchatLevelPackage: 0,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    ...mapState('dashboard', ['profile']),
    app_version() {
      return process.env.VUE_APP_VERSION
    },
    access() {
      return secureLs.getItem('userData')
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark',
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        // eslint-disable-next-line
        document.documentElement
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          // eslint-disable-next-line
          document.documentElement
          // eslint-disable-next-line
        ).value.slice(0, -2)
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  async mounted() {
    const path = window.location.pathname
    if (safePages.some(page => path.startsWith(page.path))) return
    if (secureLs.getItem('userData') && secureCookies.getItem(useJwt.jwtConfig.storageTokenKeyName)) {
      if (this.profile.partner_id === undefined) await this.$store.dispatch('dashboard/getProfile')
      if (this.profile.role_id === 5) await this.$store.dispatch('pickup/getPartnerVerified')

      // Update Ability Data
      this.userData = secureLs.getItem('userData')
      await this.fetchProfile()
    }
    window.addEventListener('message', this.watchIframe)
  },
  destroyed() {
    window.removeEventListener('message', this.watchIframe)
  },
  methods: {
    watchIframe(event) {
      const token = useJwt.getToken()
      const path = window.location.pathname
      if (safePages.some(page => path.startsWith(page.path)) || path === '/') return

      const { type } = event.data
      if (type === 'RELOAD_PAGE') {
        window.location.reload()
        return
      }
      if (event.origin !== process.env.VUE_APP_BASE_AUTH_MODULE || !type || type !== 'REFRESH_TOKEN') return

      // If token exists but expired, try refresh before giving up
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          const now = Math.floor(Date.now() / 1000)
          if (payload.exp < now) {
            // Token expired — attempt refresh instead of immediate redirect
            useJwt.refreshToken().then(r => {
              if (r.data.status === false) {
                secureLs.clearItem()
                secureCookies.clearItem()
                this.$router.replace('/login')
              } else {
                const newToken = r.data.access_token
                try {
                  const newPayload = JSON.parse(atob(newToken.split('.')[1]))
                  const expiresInDays = (newPayload.exp - Math.floor(Date.now() / 1000)) / 86400
                  useJwt.setToken(newToken, expiresInDays > 0 ? expiresInDays : 1)
                } catch (e) {
                  useJwt.setToken(newToken, 1)
                }
                this.sendTokenToIframe(newToken)
              }
            }).catch(() => {
              secureLs.clearItem()
              secureCookies.clearItem()
              this.$router.replace('/login')
            })
            return
          }
        } catch (e) {
          console.error(e)
          // JWT parsing failed, proceed with token as-is
        }
      }

      // No token at all — redirect to login
      if (!token) {
        this.logoutAndRedirectToLoginPage()
        this.logoutAuthModule()
      }

      this.sendTokenToIframe(token)
    },
    logoutAndRedirectToLoginPage() {
      store.commit('dashboard/UPDATE_ِِMY_PROFILE', [])

      this.$store.commit('komcards/UPDATE_VIEW_LIST', 'grid')

      removeLocalStorageLogout()

      // Reset ability
      this.$ability.update(initialAbility)
      // Redirect to login page
      this.$router.push({ name: 'auth-login' })
    },
    logoutAuthModule() {
      const iframe = document.createElement('iframe')
      iframe.src = `${process.env.VUE_APP_BASE_AUTH_MODULE}/logout`
      iframe.width = '0'
      iframe.height = '0'
      iframe.style.display = 'none'
      document.body.appendChild(iframe)
    },
    sendTokenToIframe(token) {
      const iframes = document.querySelectorAll('iframe')
      const matched = Array.from(iframes).find(iframe => iframe.src.includes(process.env.VUE_APP_BASE_AUTH_MODULE))
      if (!matched || !token) return

      matched.contentWindow.postMessage({ token }, '*')
    },
    async fetchProfile() {
      try {
        this.$http
          .post('/user/get-profile', {
            user_id: this.userData.id,
          }).then(async response => {
            this.profiles = response.data.data[0] ? response.data.data[0] : response.data.data
            if (this.profiles.role_name.toUpperCase() === 'TALENT GLOBAL') {
              await this.fetchMenus()
            } else {
              this.applyUpdate()
            }
            this.$store.commit('dashboard/UPDATE_PROFILE', this.profiles)
          }).catch(err => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                text: err,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            }, 1000)
          })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: err,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        }, 1000)
      }
    },

    fetchMenus() {
      try {
        this.$http_new
          .get('/auth/api/v1/teams/menu/features').then(async response => {
            this.menus = response.data.data
            await this.applyUpdate()
          }).catch(err => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                text: err,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            }, 1000)
          })
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: err,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        }, 1000)
      }
    },

    async applyUpdate() {
      const role = this.profiles.role_name.toUpperCase()
      const komship = this.profiles.is_komship === 1
      const kompack = this.profiles.is_kompack === 1
      const komcards = this.profiles.is_komcards === 1
      const komchat = this.profiles.is_komchat === 1
      const komads = this.profiles.is_komads === 1
      const komtim = this.profiles.is_komtim === 1
      const komstore = this.profiles.is_komstore === 1
      if (komchat) {
        await this.$store.dispatch('whatsapp/getUpdateDashboardWa')
        this.komchatLevelPackage = this.$store.state.whatsapp.dashboard_wa?.subscription_plan_level
      }
      const isAnyInvoice = this.profiles.unpaid_invoice_exists

      let ability = []

      switch (role) {
        case ('PARTNER'):
          if (komship) {
            ability = [
              { action: 'manage', subject: 'Komship TalentPool' },
              { action: 'manage', subject: 'Komship Wishlist' },
              { action: 'manage', subject: 'PartnerProfile' },
              { action: 'read', subject: 'Dashboard Komship' },
              { action: 'manage', subject: 'Customer' },
              { action: 'manage', subject: 'Info Customer' },
              { action: 'manage', subject: 'Data Customer' },
              { action: 'manage', subject: 'Produk' },
              { action: 'manage', subject: 'Tambah Produk' },
              { action: 'manage', subject: 'Data Produk' },
              { action: 'manage', subject: 'Order' },
              { action: 'manage', subject: 'Tambah Order' },
              { action: 'manage', subject: 'Data Order' },
              { action: 'manage', subject: 'Pickup' },
              { action: 'manage', subject: 'Ajukan Pickup' },
              { action: 'manage', subject: 'History Pickup' },
              { action: 'manage', subject: 'Keuangan' },
              { action: 'manage', subject: 'Penghasilan' },
              { action: 'manage', subject: 'Saldo' },
              { action: 'manage', subject: 'Saldo Detail' },
              { action: 'manage', subject: 'Setting Komship' },
              { action: 'manage', subject: 'Setting Profile' },
              { action: 'manage', subject: 'Team' },
              { action: 'manage', subject: 'Talent' },
              { action: 'manage', subject: 'Setting Pickup Address' },
              { action: 'manage', subject: 'Setting Rekening Bank' },
              { action: 'manage', subject: 'Setting PIN' },
              { action: 'manage', subject: 'Setting Ekspedisi' },
              { action: 'manage', subject: 'Hiring' },
              { action: 'manage', subject: 'Fitur Pendukung' },
              { action: 'manage', subject: 'MyApps' },
              { action: 'manage', subject: 'Kendala' },
              { action: 'manage', subject: 'Tiket Kendala' },
              { action: 'manage', subject: 'Monitoring Claim' },
              { action: 'manage', subject: 'Inbox' },
              { action: 'manage', subject: 'Insight' },
            ]
            if (kompack) {
              ability.push({ action: 'manage', subject: 'Gudangku' })
              ability.push({ action: 'manage', subject: 'Gudang' })
              ability.push({ action: 'manage', subject: 'Cari Gudang' })
              ability.push({ action: 'manage', subject: 'Ajukan Inbound' })
              if (this.isEligibleForKompackMarketplace(this.userData?.id)) {
                ability.push({ action: 'manage', subject: 'Integrasi Marketplace' })
                ability.push({ action: 'manage', subject: 'Marketplace Transaction' })
              }
              ability.push({
                action: 'manage',
                subject: 'Riwayat Pengajuan',
              })
              ability.push({ action: 'manage', subject: 'Tagihan' })
              ability.push({ action: 'manage', subject: 'Kompack Partner Onboarding' })
            }
            if (!kompack) {
              ability.push({ action: 'manage', subject: 'Gudangku' })
              ability.push({ action: 'manage', subject: 'Gudang' })
            }
            if (komstore) {
              ability.push({ action: 'manage', subject: 'Komstore' })
              ability.push({ action: 'manage', subject: 'Toko Online' })
              ability.push({ action: 'manage', subject: 'Online Store' })
              ability.push({ action: 'manage', subject: 'Komstore Partner Onboarding' })
            }
            if (komcards) {
              ability.push({ action: 'manage', subject: 'KartuKomcards' })
            }
            if (isAnyInvoice) {
              ability.push({ action: 'manage', subject: 'Invoice' })
              ability.push({ action: 'manage', subject: 'Invoice Detail' })
            }
            if (this.profiles.total_sdm_assigned > 0) {
              ability.push({ action: 'manage', subject: 'Presensi Talent' })
            }
          }

          if (komstore) {
            ability.push({ action: 'manage', subject: 'Komstore' })
            ability.push({ action: 'manage', subject: 'Toko Online' })
            ability.push({ action: 'manage', subject: 'Online Store' })
            ability.push({ action: 'manage', subject: 'Komstore Partner Onboarding' })
            ability.push({ action: 'read', subject: 'Dashboard Komship' })
            ability.push({ action: 'manage', subject: 'Produk' })
            ability.push({ action: 'manage', subject: 'Tambah Produk' })
            ability.push({ action: 'manage', subject: 'Data Produk' })
            ability.push({ action: 'manage', subject: 'Gudang' })
            ability.push({ action: 'manage', subject: 'Gudangku' })
            ability.push({ action: 'manage', subject: 'Keuangan' })
            ability.push({ action: 'manage', subject: 'Saldo' })
            ability.push({ action: 'manage', subject: 'Saldo Detail' })
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Setting Payment' })
            ability.push({ action: 'manage', subject: 'Setting Rekening Bank' })
            ability.push({ action: 'manage', subject: 'Setting PIN' })
            ability.push({ action: 'manage', subject: 'MyApps' })
          }

          if (kompack) {
            // Gudang
            ability.push({ action: 'manage', subject: 'Gudangku' })
            ability.push({ action: 'manage', subject: 'Gudang' })
            ability.push({ action: 'manage', subject: 'Cari Gudang' })
            ability.push({ action: 'manage', subject: 'Ajukan Inbound' })
            if (this.isEligibleForKompackMarketplace(this.userData?.id)) {
              ability.push({ action: 'manage', subject: 'Integrasi Marketplace' })
              ability.push({ action: 'manage', subject: 'Marketplace Transaction' })
            }
            ability.push({ action: 'manage', subject: 'Riwayat Pengajuan' })
            // Produk
            ability.push({ action: 'manage', subject: 'Produk' })
            ability.push({ action: 'manage', subject: 'Tambah Produk' })
            ability.push({ action: 'manage', subject: 'Data Produk' })
            // Pickup
            ability.push({ action: 'manage', subject: 'Pickup' })
            ability.push({ action: 'manage', subject: 'Ajukan Pickup' })
            ability.push({ action: 'manage', subject: 'History Pickup' })
            // Keuangan
            ability.push({ action: 'manage', subject: 'Keuangan' })
            ability.push({ action: 'manage', subject: 'Saldo' })
            ability.push({ action: 'manage', subject: 'Saldo Detail' })
            // Pengaturan
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Team' })
            ability.push({ action: 'manage', subject: 'Talent' })
            ability.push({ action: 'manage', subject: 'Setting Rekening Bank' })
            ability.push({ action: 'manage', subject: 'Setting PIN' })
            // Aplikasiku
            ability.push({ action: 'manage', subject: 'MyApps' })
            ability.push({ action: 'manage', subject: 'Tagihan' })
            ability.push({ action: 'manage', subject: 'Kompack Partner Onboarding' })
            if (isAnyInvoice) {
              ability.push({ action: 'manage', subject: 'Invoice' })
              ability.push({ action: 'manage', subject: 'Invoice Detail' })
            }
            if (this.profiles.total_sdm_assigned > 0) {
              ability.push({ action: 'manage', subject: 'Presensi Talent' })
            }
          }

          if (komcards) {
            ability.push({ action: 'read', subject: 'Dashboard Komship' })
            ability.push({ action: 'manage', subject: 'Keuangan' })
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Setting Pickup Address' })
            ability.push({ action: 'manage', subject: 'Setting Rekening Bank' })
            ability.push({ action: 'manage', subject: 'Setting PIN' })
            ability.push({ action: 'manage', subject: 'MyApps' })
            ability.push({ action: 'manage', subject: 'Saldo Detail' })
            ability.push({ action: 'manage', subject: 'KartuKomcards' })
            ability.push({ action: 'manage', subject: 'Team' })
            ability.push({ action: 'manage', subject: 'Talent' })
            if (isAnyInvoice) {
              ability.push({ action: 'manage', subject: 'Invoice' })
              ability.push({ action: 'manage', subject: 'Invoice Detail' })
            }
            if (this.profiles.total_sdm_assigned > 0) {
              ability.push({ action: 'manage', subject: 'Presensi Talent' })
            }
          }

          if (komchat) {
            ability.push({ action: 'manage', subject: 'Fitur Pendukung' })
            ability.push({ action: 'manage', subject: 'Group Contact' })
            ability.push({ action: 'manage', subject: 'Broadcast' })
            ability.push({ action: 'manage', subject: 'Customer' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'MyApps' })
            ability.push({ action: 'manage', subject: 'Keuangan' })
            ability.push({ action: 'manage', subject: 'Saldo' })
            ability.push({ action: 'manage', subject: 'Saldo Detail' })
            ability.push({ action: 'manage', subject: 'Inbox Komchat' })
            if (this.komchatLevelPackage >= 2) {
              ability.push({ action: 'manage', subject: 'Produk' })
              ability.push({ action: 'manage', subject: 'Tambah Produk' })
              ability.push({ action: 'manage', subject: 'Data Produk' })
              ability.push({ action: 'manage', subject: 'Gudang' })
              ability.push({ action: 'manage', subject: 'Gudangku' })
              ability.push({ action: 'manage', subject: 'AI Customer Service' })
              ability.push({ action: 'manage', subject: 'Order' })
              ability.push({ action: 'manage', subject: 'Order Ai' })
              ability.push({ action: 'manage', subject: 'Setting Payment' })
            }
          }

          if (komads) {
            ability.push({ action: 'read', subject: 'Dashboard Komship' })
            ability.push({ action: 'manage', subject: 'Marketing' })
            ability.push({ action: 'manage', subject: 'Whitelisted Account' })
            ability.push({ action: 'manage', subject: 'Saldo' })
            ability.push({ action: 'manage', subject: 'Saldo Detail' })
            ability.push({ action: 'manage', subject: 'Keuangan' })
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Setting Rekening Bank' })
            ability.push({ action: 'manage', subject: 'Setting PIN' })
            ability.push({ action: 'manage', subject: 'MyApps' })
          }

          if (komtim) {
            ability.push({ action: 'manage', subject: 'Komtim Partner Onboarding' })
            ability.push({ action: 'manage', subject: 'Team' })
            ability.push({ action: 'manage', subject: 'Talent' })
            ability.push({ action: 'manage', subject: 'Keuangan' })
            ability.push({ action: 'manage', subject: 'Saldo' })
            ability.push({ action: 'manage', subject: 'Saldo Detail' })
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Setting Rekening Bank' })
            ability.push({ action: 'manage', subject: 'Setting PIN' })
            ability.push({ action: 'manage', subject: 'MyApps' })
            if (isAnyInvoice) {
              ability.push({ action: 'manage', subject: 'Invoice' })
              ability.push({ action: 'manage', subject: 'Invoice Detail' })
            }
            if (this.profiles.total_sdm_assigned > 0) {
              ability.push({ action: 'manage', subject: 'Presensi Talent' })
            }
          }

          if (this.profiles.is_komed === 1) {
            ability.push({ action: 'read', subject: 'Dashboard Komship' })
            ability.push({ action: 'manage', subject: 'Konten' })
            ability.push({ action: 'manage', subject: 'Buat Konten' })
            ability.push({ action: 'manage', subject: 'Produk' })
            ability.push({ action: 'manage', subject: 'Tambah Produk' })
            ability.push({ action: 'manage', subject: 'Data Produk' })
            ability.push({ action: 'manage', subject: 'MyApps' })
            ability.push({ action: 'manage', subject: 'Setting Komship' })
            ability.push({ action: 'manage', subject: 'Setting Profile' })
            ability.push({ action: 'manage', subject: 'Setting Rekening Bank' })
            ability.push({ action: 'manage', subject: 'Setting PIN' })
          }
          break
        case ('TALENT GLOBAL'):
          this.menus.forEach(item => {
            if (item.menu_name === 'PRODUK') {
              ability.push({ action: 'manage', subject: 'Produk' })
              const findAccessView = item.access.find(
                accessItem => accessItem.access_id === 1,
              )
              const findAccessManage = item.access.find(
                accessItem => accessItem.access_id === 2,
              )
              if (findAccessView !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Data Produk',
                  path: '/produk',
                })
              }
              if (findAccessManage !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Tambah Produk',
                  path: '/add-produk',
                })
              }
            }
            if (item.menu_name === 'ORDER') {
              ability.push({ action: 'manage', subject: 'Order' })
              const findAccessView = item.access.find(
                accessItem => accessItem.access_id === 1,
              )
              const findAccessManage = item.access.find(
                accessItem => accessItem.access_id === 2,
              )
              if (findAccessView !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Data Order',
                  path: '/data-order',
                })
              }
              if (findAccessManage !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Tambah Order',
                  path: '/add-order',
                })
              }
            }
            if (item.menu_name === 'DASHBOARD') {
              const findAccessView = item.access.find(
                accessItem => accessItem.access_id === 1,
              )
              if (findAccessView !== undefined) {
                ability.push({
                  action: 'read',
                  subject: 'Dashboard Komship',
                  path: '/',
                })
              }
            }
            if (item.menu_name === 'GUDANG') {
              const findAccessCariGudang = item.access.find(
                accessItem => accessItem.access_id === 18,
              )
              const findAccessGudangku = item.access.find(
                accessItem => accessItem.access_id === 16,
              )
              const findAccessAjukanInbound = item.access.find(
                accessItem => accessItem.access_id === 17,
              )
              const findAccessRiwayatPengajuan = item.access.find(
                accessItem => accessItem.access_id === 19,
              )
              ability.push({ action: 'manage', subject: 'Gudang' })
              if (findAccessGudangku !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Gudangku',
                  path: '/gudangku',
                })
              }
              if (findAccessCariGudang !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Cari Gudang',
                  path: '/cari-gudang',
                })
              }
              if (findAccessAjukanInbound !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Ajukan Inbound',
                  path: '/ajukan-inbound',
                })
              }

              if (this.isEligibleForKompackMarketplace(this.userData?.id)) {
                ability.push({ action: 'manage', subject: 'Integrasi Marketplace' })
                ability.push({ action: 'manage', subject: 'Marketplace Transaction' })
              }
              if (findAccessRiwayatPengajuan !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Riwayat Pengajuan',
                  path: '/riwayat-pengajuan',
                })
              }
            }
            if (item.menu_name === 'PICKUP') {
              ability.push({ action: 'manage', subject: 'Pickup' })
              const findAccessView = item.access.find(
                accessItem => accessItem.access_id === 6,
              )
              const findAccessManage = item.access.find(
                accessItem => accessItem.access_id === 5,
              )
              if (findAccessView !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'History Pickup',
                  path: '/history-pickup',
                })
              }
              if (findAccessManage !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Ajukan Pickup',
                  path: '/ajukan-pickup',
                })
              }
            }
            if (item.menu_name === 'KEUANGAN') {
              ability.push({ action: 'manage', subject: 'Keuangan' })
              const findAccessView = item.access.find(
                accessItem => accessItem.access_id === 7,
              )
              const findAccessManage = item.access.find(
                accessItem => accessItem.access_id === 8,
              )
              if (findAccessView !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Penghasilan',
                  path: '/keuangan/penghasilan',
                })
              }
              if (findAccessManage !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Saldo',
                  path: '/keuangan/saldo',
                })
              }
            }
            if (item.menu_name === 'PENGATURAN') {
              ability.push({ action: 'manage', subject: 'Setting Komship' })
              const findAccessBank = item.access.find(
                accessItem => accessItem.access_id === 12,
              )
              const findAccessPin = item.access.find(
                accessItem => accessItem.access_id === 13,
              )
              const findAccessExpedition = item.access.find(
                accessItem => accessItem.access_id === 14,
              )
              if (findAccessBank !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Setting Rekening Bank',
                  path: '/setting-kompship/rekening-bank',
                })
              }
              if (findAccessPin !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Setting PIN',
                  path: '/setting-kompship/pin',
                })
              }
              if (findAccessExpedition !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Setting Ekspedisi',
                  path: '/setting-kompship/ekspedisi',
                })
              }
            }
            if (item.menu_name === 'KENDALA') {
              ability.push({ action: 'manage', subject: 'Kendala' })
              const findAccessKendala = item.access.find(
                accessItem => [1, 2, 3, 4].includes(accessItem.access_id),
              )
              const findAccessMonitoringClaim = item.access.find(
                accessItem => accessItem.access_id === 22,
              )
              if (findAccessKendala !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Tiket Kendala',
                  path: '/ticketing',
                })
              }
              if (findAccessMonitoringClaim !== undefined) {
                ability.push({
                  action: 'manage',
                  subject: 'Monitoring Claim',
                  path: '/monitoring-claim',
                })
              }
            }
            if (item.menu_name === 'KARTU KOMCARDS') {
              ability.push({ action: 'manage', subject: 'Keuangan', path: '/komcards' })
              ability.push({ action: 'manage', subject: 'KartuKomcards', path: '/komcards' })
            }
          })
          break
        default:
          break
      }

      this.profiles.ability = ability
      this.$ability.update(ability)

      secureLs.setItem('userData', JSON.stringify(this.profiles))
      localStorage.setItem('ability', JSON.stringify(ability))
      store.commit('auth/UPDATE_USER_DATA', this.profiles)
    },
    isEligibleForKompackMarketplace(userId) {
      const rawList = process.env.VUE_APP_KOMPACK_MARKETPLACE
      if (!rawList) return true

      const allowed = rawList
        .split(',')
        .map(v => String(v).trim())
        .filter(Boolean)

      return allowed.includes(String(userId).trim())
    },
  },
}
</script>

<style>
/*
  Restore the default Vuexy #f8f8f8 grey canvas to recreate the visual boundary
  between the sidebar and the dashboard, as shown in the Figma design.
*/
html body.is-embedded-iframe,
body.is-embedded-iframe #app,
body.is-embedded-iframe .app-content {
  background-color: #f3f4f6 !important;
}

/* Force hide global Vue widgets when embedded inside React */
body.is-embedded-iframe .btn-scroll-to-top,
body.is-embedded-iframe .crisp-client,
body.is-embedded-iframe footer:not(.modal-footer),
body.is-embedded-iframe .grecaptcha-badge {
  display: none !important;
}
</style>
